import { lazy, type ComponentType } from 'react';
/** RFC 162: one quiet retry absorbs a dropped chunk request on weak signal. */
export const ROUTE_IMPORT_RETRY_DELAY_MS = 800;

async function importWithRetry<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch {
    await new Promise(resolve => setTimeout(resolve, ROUTE_IMPORT_RETRY_DELAY_MS));
    return load();
  }
}

export function createRouteLoader<T>(load: () => Promise<T>) {
  let pending: Promise<T> | undefined;
  return () => {
    if (!pending) {
      pending = importWithRetry(load).catch(error => {
        pending = undefined;
        throw error;
      });
    }
    return pending;
  };
}

export const primaryRouteLoaders = {
  '/translate': createRouteLoader(() => import('@/pages/Translate')),
  '/vocabulary': createRouteLoader(() => import('@/pages/Vocabulary')),
  '/study': createRouteLoader(() => import('@/pages/Study')),
  '/conjugation': createRouteLoader(() => import('@/pages/Conjugation')),
};

export function preloadPrimaryRoute(path: string): void {
  if (!(path in primaryRouteLoaders)) return;
  const load = primaryRouteLoaders[path as keyof typeof primaryRouteLoaders];
  void load().catch(() => {});
}
/** Lazy page component sharing the deduplicated, retrying route loader. */
export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(createRouteLoader(load));
}
